<template>
  <FormGroup
    name="login"
    class="login-form"
    layout="vertical"
    :wrapper-col="{ span: 24 }"
    :model="loginForm"
    @finish="submitForm"
    :loading="loading"
  >
    <a-row class="ant-row ant-row-center login-form__email-row">
      <a-col :span="24">
        <!-- custom validate status for API error -->
        <a-form-item
          name="username"
          :rules="[
            makeRequiredRule(t('Please enter Email')),
            !overrideEmailValidation &&
              makeEmailRule(t('Please enter a valid Email')),
            makeLatinCharRule(),
          ]"
          :validateFirst="true"
          :validateStatus="!!tokenAuthParsedError && 'error'"
        >
          <TextInput
            v-model:value="loginForm.username"
            :label="t('Email')"
            @change="resetErrorMessage"
            type="email"
          />
        </a-form-item>
      </a-col>
    </a-row>
    <a-row class="ant-row ant-row-center">
      <a-col :span="24">
        <!-- custom validate status for API error -->
        <a-form-item
          :class="[
            'login-form__password',
            !!tokenAuthParsedError && 'form__api-error',
          ]"
          name="password"
          :rules="[
            makeRequiredRule(t('Please enter Password')),
            makeLatinCharRule(),
          ]"
          :validateStatus="!!tokenAuthParsedError && 'error'"
        >
          <PasswordInput
            :label="t('Password')"
            v-bind="$attrs"
            v-model:value="loginForm.password"
            :disableWhileLoading="loading"
            @change="resetErrorMessage"
          />
        </a-form-item>
      </a-col>
    </a-row>

    <!-- specifically for this case, let’s use under field error-->
    <div class="form__error-message">
      <a-typography-text type="danger">
        {{ tokenAuthParsedError }}
      </a-typography-text>
    </div>

    <vue-recaptcha
      class="login-form__captcha"
      ref="vueRecaptcha"
      v-if="captcha.isCaptchaEnabled"
      :siteKey="captcha.recaptchaSiteKey"
      size="invisible"
      theme="dark"
      :tabindex="0"
      :loading-timeout="config.loadingTimeoutInMs"
      @verify="captcha.recaptchaVerified"
      @expire="captcha.recaptchaError"
      @fail="captcha.recaptchaError"
    >
    </vue-recaptcha>

    <a-row class="ant-row ant-row-center login-form__button">
      <!-- Forgot Password -->
      <a-col :xs="24">
        <BasicButton :disabled="loading" @click="forgotPassword">
          {{ t("Forgot password?") }}
        </BasicButton>
      </a-col>
      <!-- Log in -->
      <a-col class="mb-8" :xs="24">
        <FormSubmitButton :loading="loading">
          {{ t("Log in") }}
        </FormSubmitButton>
      </a-col>
      <!-- Create new account -->
      <a-col class="mb-8" :xs="24">
        <!-- No need to check logic for SSO login on whether to show register or not -->
        <BasicButton
          id="login-form__registration"
          :disabled="loading"
          @click="createNewAccount"
        >
          {{ t("Create new account") }}
        </BasicButton>
      </a-col>
      <!-- Retain "Login to business/personal account", see #862jedhyg -->
      <a-col :xs="24">
        <BasicButton :disabled="loading" @click="onFooterBtnClick">
          <template #icon>
            <img :src="AccountLinkIcon" class="px-10 pb-3" />
          </template>
          {{ loginBtnText }}
        </BasicButton>
      </a-col>
    </a-row>
  </FormGroup>
</template>

<style lang="scss">
.login-form {
  &__captcha {
    display: none;
  }
  &__button {
    margin-top: $margin-md;
    .form-submit-button {
      min-width: 100%;
      margin-top: $margin-lg;
    }
    .basic-button {
      width: 100%;
      height: $btn-height;
      font-weight: $btn-font-weight;
      img {
        filter: $non-auth-icon-filter;
        vertical-align: initial; // fixes icon alignment
      }
    }
  }
  &__email-row {
    margin-top: $non-auth-margin;
  }
}
</style>

<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  makeEmailRule,
  makeRequiredRule,
  makeLatinCharRule,
} from "@/shared/utils/validators/commonValidators";
import { useRouter } from "vue-router";
import useCaptcha from "@/shared/composables/useCaptcha";
import FormGroup from "@/shared/components/forms/FormGroup.vue";
import PasswordInput from "@/shared/components/forms/PasswordInput.vue";
import VueRecaptcha from "vue3-recaptcha2";
import routeNames from "@/shared/router/routeNames";
import FormSubmitButton from "@/shared/components/buttons/FormSubmitButton.vue";
import BasicButton from "@/shared/components/buttons/BasicButton.vue";
import TextInput from "@/shared/components/forms/TextInput.vue";
import { useTokenAuthenticate } from "@/shared/composables/api/useTokenAuthenticate";
import { config, isCorporateSite } from "@/shared/utils/config";
import AccountLinkIcon from "@/assets/images/login/account-link-logo-icon.svg";
import { GqlError } from "@/shared/utils/graphql/errorHandler";
import { apiErrorCodes } from "@/shared/utils/constants";
import find from "lodash/find";
import { useGtm, GtmSupport } from "@gtm-support/vue-gtm";

export default defineComponent({
  emits: ["onOtpRequired"],
  props: {
    useTokenAuthenticate: {
      type: Boolean,
      default: true,
    },
    /** FIXME: Remove in case not needed anymore;
     * applied to override accepting valid email
     * for SSO - Login since it only temporarily
     * accepts admin as valid username
     */
    overrideEmailValidation: Boolean,
  },
  setup(props, { emit }) {
    const t = useI18n().t;
    const vueRecaptcha = ref();
    const router = useRouter();
    const gtm = useGtm() as GtmSupport;
    const loginForm = reactive({
      username: "",
      password: "",
    });
    const tokenAuthParsedError = ref<string>("");

    const { tokenAuthenticateLoading, tokenAuthenticate, otpRequired } =
      useTokenAuthenticate();

    // called on script so we can reset error message
    const submitForm = () => {
      resetErrorMessage();
      captcha.captchaHandler();
    };

    const submitLogin = async (captcha, showError?: boolean) => {
      resetErrorMessage();
      try {
        if (props.useTokenAuthenticate) {
          await tokenAuthenticate(
            captcha,
            showError,
            loginForm.username,
            loginForm.password
          );
        }
      } catch (parsedError) {
        const gqlErrors = (parsedError as GqlError)?.errors;
        const hasGqlError = gqlErrors?.length > 0;

        // check if parsed error is not a fallback error message
        if (hasGqlError) {
          // Specific error message for INVALID INPUT

          const gqlErrorInvalidInput = find(gqlErrors, [
            "code",
            apiErrorCodes.INVALID_INPUT,
          ]);
          if (gqlErrorInvalidInput) {
            if (gqlErrorInvalidInput?.displayMessage) {
              return (tokenAuthParsedError.value =
                gqlErrorInvalidInput?.displayMessage);
            }
            return (tokenAuthParsedError.value = t(
              "Your email or password is incorrect. Please try again or reset your password"
            ));
          } else {
            // else, return error message returned by BE
            return (tokenAuthParsedError.value = gqlErrors[0]?.message);
          }
        }

        // return fallback error message
        return (tokenAuthParsedError.value = String(parsedError));
      }
    };

    const captcha = useCaptcha(submitLogin, vueRecaptcha);

    // Emit otpRequired value
    watch([otpRequired], ([value]) => {
      emit("onOtpRequired", value);
    });

    const forgotPassword = () => {
      resetErrorMessage();
      router.push({ name: routeNames.forgotPassword });
    };

    const createNewAccount = () => {
      resetErrorMessage();
      // Trigger Google Tag Manager custom event
      gtm.trackEvent({
        event: "registration_click",
        action: "click",
      });
      router.push({ name: routeNames.register });
    };

    const onFooterBtnClick = () => {
      resetErrorMessage();
      window.location.href = isCorporateSite
        ? config.personalCabinetUrl ?? ""
        : config.corporateCabinetUrl ?? "";
    };

    const loginBtnText = t("Login to {cabinet} account", {
      cabinet: isCorporateSite ? t("personal") : t("business"),
    });

    const resetErrorMessage = () => {
      tokenAuthParsedError.value = "";
    };

    return {
      t,
      loginForm,
      tokenAuthenticate,
      captcha,
      vueRecaptcha,
      otpRequired,
      forgotPassword,
      tokenAuthenticateLoading,
      submitLogin,
      createNewAccount,
      onFooterBtnClick,
      AccountLinkIcon,
      loginBtnText,
      loading: computed(
        () =>
          tokenAuthenticateLoading?.value || captcha.vueRecaptchaLoading.value
      ),
      tokenAuthParsedError,
      resetErrorMessage,
      submitForm,
      config,
    };
  },
  methods: {
    makeRequiredRule,
    makeEmailRule,
    makeLatinCharRule,
  },
  components: {
    FormGroup,
    TextInput,
    PasswordInput,
    FormSubmitButton,
    BasicButton,
    VueRecaptcha,
  },
});
</script>
